// Team member role definitions
export interface TeamMember {
    title: string;
    quantity: number;
    hourlyRate: number;
}

export interface TeamComposition {
    [key: string]: TeamMember;
}

export interface ServiceTeamImpact {
    members: {
        [key: string]: {
            quantity: number;
            title: string;
            hourlyRate: number;
        }
    };
}

// Team composition with hourly rates
export const TEAM_COMPOSITION: TeamComposition = {
    projectManager: { title: 'Kierownik Projektu', quantity: 0, hourlyRate: 0 },
    designer: { title: 'Projektant UI/UX', quantity: 0, hourlyRate: 0 },
    developer: { title: 'Programista', quantity: 0, hourlyRate: 70 },
    developer2: { title: 'Programista BE', quantity: 0, hourlyRate: 70 },
    devops: { title: 'Specjalista DevOps', quantity: 0, hourlyRate: 70 },
    tester: { title: 'Tester', quantity: 0, hourlyRate: 0 },
    mlEngineer: { title: 'Inżynier ML', quantity: 0, hourlyRate: 90 },
    dataScientist: { title: 'Data Scientist', quantity: 0, hourlyRate: 90 },
    consultant: { title: 'Konsultant', quantity: 0, hourlyRate: 70 },
};

// Map services to team composition impact
export const SERVICE_TEAM_MAP: Record<string, ServiceTeamImpact> = {
    'Projektowanie wyglądu i funkcji aplikacji (UX/UI)': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            designer: { quantity: 1, title: 'Projektant UI/UX', hourlyRate: 0 }
        }
    },
    'Aplikacja mobilna': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            tester: { quantity: 1, title: 'Tester', hourlyRate: 0 },
            developer: { quantity: 1, title: 'Programista', hourlyRate: 70 }
        }
    },
    'Aplikacja przeglądarkowa': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            tester: { quantity: 1, title: 'Tester', hourlyRate: 0 },
            developer: { quantity: 1, title: 'Programista', hourlyRate: 70 }
        }
    },
    'Aplikacja desktopowa (tradycyjne oprogramowanie na komputer)': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            tester: { quantity: 1, title: 'Tester', hourlyRate: 0 },
            developer: { quantity: 1, title: 'Programista', hourlyRate: 70 }
        }
    },
    'Chmura i zaplecze': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            tester: { quantity: 1, title: 'Tester', hourlyRate: 0 },
            developer2: { quantity: 1, title: 'Programista BE', hourlyRate: 70 },
            devops: { quantity: 1, title: 'Specjalista DevOps', hourlyRate: 70 }
        }
    },
    'Wykorzystanie sztucznej inteligencji': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            tester: { quantity: 1, title: 'Tester', hourlyRate: 0 },
            mlEngineer: { quantity: 1, title: 'Inżynier ML', hourlyRate: 90 }
        }
    },
    'Analiza danych (Data Science)': {
        members: {
            projectManager: { quantity: 1, title: 'Kierownik Projektu', hourlyRate: 0 },
            tester: { quantity: 1, title: 'Tester', hourlyRate: 0 },
            dataScientist: { quantity: 1, title: 'Data Scientist', hourlyRate: 90 }
        }
    },
    'Pomoc w rejestracji wyrobów medycznych': {
        members: {
            consultant: { quantity: 1, title: 'Konsultant', hourlyRate: 70 }
        }
    }
};

// Define multipliers for each role based on challenge type
export const ROLE_CHALLENGE_MULTIPLIERS: Record<string, Record<string, number>> = {
    'Przetestowanie pomysłu (Proof of concept)': {
        projectManager: 1,
        tester: 1,
        designer: 1,
        developer: 1,
        developer2: 0.5,
        mlEngineer: 1,
        dataScientist: 1,
        consultant: 1,
        devops: 0.1 // 0,1 from CSV
    },
    'Zbudować MVP (Minimalną Wersję Produktu)': {
        projectManager: 1,
        tester: 1,
        designer: 1,
        developer: 1,
        developer2: 1,
        mlEngineer: 1,
        dataScientist: 1,
        consultant: 1,
        devops: 0.25 // 0,25 from CSV
    },
    'Stworzyć zaawansowany produkt': {
        projectManager: 1,
        tester: 1,
        designer: 1,
        developer: 2,
        developer2: 2,
        mlEngineer: 2,
        dataScientist: 2,
        consultant: 1,
        devops: 0.25 // 0,25 from CSV
    },
    'Rozbudować istniejący produkt': {
        projectManager: 1,
        tester: 1,
        designer: 1,
        developer: 2,
        developer2: 2,
        mlEngineer: 2,
        dataScientist: 2,
        consultant: 1,
        devops: 0.25 // 0,25 from CSV
    },
    'Stworzyć system korporacyjny': {
        projectManager: 1,
        tester: 1,
        designer: 1,
        developer: 3,
        developer2: 3,
        mlEngineer: 2,
        dataScientist: 2,
        consultant: 2,
        devops: 1
    }
};

// Workshop types and costs
export const WORKSHOP_TYPES = [
    'Polska',
    'Europa',
    'USA',
    'Inne'
];

// Define exchange rates for different currencies
export const EXCHANGE_RATES: Record<string, { currency: string; rate: number }> = {
    'Polska': { currency: 'PLN', rate: 4 },
    'USA': { currency: 'USD', rate: 1.2 },
    'Europa': { currency: 'EUR', rate: 1 },
    'Inne': { currency: 'EUR', rate: 1 }
};

// Challenge options
export const CHALLENGES = [
    'Przetestowanie pomysłu (Proof of concept)',
    'Zbudować MVP (Minimalną Wersję Produktu)',
    'Stworzyć zaawansowany produkt',
    'Rozbudować istniejący produkt',
    'Stworzyć system korporacyjny'
];

// Service options
export const SERVICES = [
    'Projektowanie wyglądu i funkcji aplikacji (UX/UI)',
    'Aplikacja mobilna',
    'Aplikacja przeglądarkowa',
    'Aplikacja desktopowa (tradycyjne oprogramowanie na komputer)',
    'Wykorzystanie sztucznej inteligencji',
    'Analiza danych (Data Science)',
    'Chmura i zaplecze',
    'Pomoc w rejestracji wyrobów medycznych'
];

// Define challenge durations in months
export const CHALLENGE_DURATION_MAP: Record<string, number> = {
    'Przetestowanie pomysłu (Proof of concept)': 1,
    'Zbudować MVP (Minimalną Wersję Produktu)': 4,
    'Stworzyć zaawansowany produkt': 6,
    'Rozbudować istniejący produkt': 6,
    'Stworzyć system korporacyjny': 12,
};

// Define challenge design costs in EUR
export const CHALLENGE_DESIGN_COST: Record<string, number> = {
    'Przetestowanie pomysłu (Proof of concept)': 1000,
    'Zbudować MVP (Minimalną Wersję Produktu)': 4000,
    'Stworzyć zaawansowany produkt': 8000,
    'Rozbudować istniejący produkt': 8000,
    'Stworzyć system korporacyjny': 12000,
};

// Define challenge milestone counts
export const CHALLENGE_MILESTONES: Record<string, number> = {
    'Przetestowanie pomysłu (Proof of concept)': 1,
    'Zbudować MVP (Minimalną Wersję Produktu)': 2,
    'Stworzyć zaawansowany produkt': 3,
    'Rozbudować istniejący produkt': 3,
    'Stworzyć system korporacyjny': 6,
};

// Define challenge tranche counts
export const CHALLENGE_TRANCHES: Record<string, number> = {
    'Przetestowanie pomysłu (Proof of concept)': 2,
    'Zbudować MVP (Minimalną Wersję Produktu)': 3,
    'Stworzyć zaawansowany produkt': 4,
    'Rozbudować istniejący produkt': 4,
    'Stworzyć system korporacyjny': 7,
};

// Mapping functions to convert Polish display texts to API enum values
export const mapChallengeToApiValue = (polishChallenge: string): string => {
    const challengeMap: Record<string, string> = {
        'Przetestowanie pomysłu (Proof of concept)': 'poc',
        'Zbudować MVP (Minimalną Wersję Produktu)': 'mvp',
        'Stworzyć zaawansowany produkt': 'advanced',
        'Rozbudować istniejący produkt': 'expand',
        'Stworzyć system korporacyjny': 'enterprise'
    };
    return challengeMap[polishChallenge] || 'poc';
};

export const mapServiceToApiValue = (polishService: string): string => {
    const serviceMap: Record<string, string> = {
        'Projektowanie wyglądu i funkcji aplikacji (UX/UI)': 'design',
        'Aplikacja mobilna': 'mobile_app',
        'Aplikacja przeglądarkowa': 'web_app',
        'Aplikacja desktopowa (tradycyjne oprogramowanie na komputer)': 'desktop_app',
        'Wykorzystanie sztucznej inteligencji': 'ai',
        'Analiza danych (Data Science)': 'data_science',
        'Chmura i zaplecze': 'cloud_be',
        'Pomoc w rejestracji wyrobów medycznych': 'medical_device'
    };
    return serviceMap[polishService] || 'design';
};

export const mapCompanyLocationToApiValue = (polishLocation: string): string => {
    const locationMap: Record<string, string> = {
        'Polska': 'pl',
        'Europa': 'eu',
        'USA': 'us',
        'Inne': 'other'
    };
    return locationMap[polishLocation] || 'other';
};
